import { API_BASE_URL } from "../apiConfig";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Login.css";

const Login = () => {
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const handleLogin = async () => {
    setError("");
    setSuccessMessage("");

    if (!username || !password) {
      setError("All fields are required");
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE_URL}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          username, 
          password, 
          role: role || "" // Send empty string if role is not selected (for admin)
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || "Login failed");
        return;
      }

      // Use sessionStorage to avoid overwrite
      const sessionKey =
        data.role === "driver"
          ? `driver_${data.username}`
          : `user_${data.username}`;

      sessionStorage.setItem(
        "currentUser",
        JSON.stringify({
          _id: data._id,
          username: data.username,
          role: data.role,
          companyName: data.companyName || null,
          sessionKey,
          loginAt: Date.now(),
        })
      );

      setSuccessMessage("Login successful! Redirecting...");

      setTimeout(() => {
        if (data.role === "driver") {
          navigate("/driver/driver-dashboard");
        } else if (data.role === "admin") {
          navigate("/admin/admin-dashboard");
        } else if (data.role === "recycling_company") {
          navigate("/recycling/dashboard");
        } else {
          navigate("/user/user-dashboard");
        }
      }, 1000);
    } catch (err) {
      setError("Server error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h3 className="title">Login</h3>

        {/* Username */}
        <div className="input-wrapper">
          <label className="input-label">Username</label>
          <input
            className="input-field"
            placeholder="Enter your username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <span className="input-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
              <circle cx="12" cy="7" r="4" />
              <path d="M5.5 21c0-4.5 7-4.5 7-4.5s7 0 7 4.5" />
            </svg>
          </span>
        </div>

        {/* Password */}
        <div className="input-wrapper">
          <label className="input-label">Password</label>
          <input
            type={showPassword ? "text" : "password"}
            className="input-field"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span
            className="input-icon"
            onClick={() => setShowPassword(!showPassword)}
            style={{ cursor: "pointer" }}
          >
            {showPassword ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
            )}
          </span>
        </div>

        {/* Roles */}
        <div className="roles">
          <div
            className={`role-box ${role === "user" ? "active" : ""}`}
            onClick={() => setRole("user")}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="7" r="4" />
              <path d="M5.5 21c0-4.5 7-4.5 7-4.5s7 0 7 4.5" />
            </svg>
            <p>User</p>
          </div>

          <div
            className={`role-box ${role === "driver" ? "active" : ""}`}
            onClick={() => setRole("driver")}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="1" y="3" width="15" height="13" />
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
              <circle cx="5.5" cy="18.5" r="2.5" />
              <circle cx="18.5" cy="18.5" r="2.5" />
            </svg>
            <p>Driver</p>
          </div>

          <div
            className={`role-box ${role === "recycling_company" ? "active" : ""}`}
            onClick={() => setRole("recycling_company")}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1 4 1 10 7 10" />
              <polyline points="23 20 23 14 17 14" />
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
            </svg>
            <p>Recycling</p>
          </div>
        </div>

        {error && <p className="error-text2">{error}</p>}
        {successMessage && <p className="success-text2">{successMessage}</p>}

        <button className="login-btn" onClick={handleLogin} disabled={loading}>
          {loading ? "Logging in..." : "Login"}
        </button>

        <p className="signup-text">
          Don’t have an account?{" "}
          <span className="signup-link" onClick={() => navigate("/signup")}>
            Signup
          </span>
        </p>
      </div>
    </div>
  );
};

export default Login;